import type { StockfishPv } from "@/pages/analysis/engine/stockfishTypes";

function readNumber(value: string | undefined) {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseStockfishInfo(line: string): StockfishPv | null {
  if (!line.startsWith("info ")) {
    return null;
  }

  const tokens = line.trim().split(/\s+/);
  const parsed: StockfishPv = {
    multipv: 1,
    depth: 0,
    pv: [],
  };

  for (let index = 1; index < tokens.length; index += 1) {
    const token = tokens[index];

    if (token === "depth") {
      parsed.depth = readNumber(tokens[index + 1]) ?? parsed.depth;
      index += 1;
      continue;
    }

    if (token === "multipv") {
      parsed.multipv = readNumber(tokens[index + 1]) ?? parsed.multipv;
      index += 1;
      continue;
    }

    if (token === "score") {
      const scoreType = tokens[index + 1];
      const scoreValue = readNumber(tokens[index + 2]);
      if (scoreType === "cp" && scoreValue !== undefined) {
        parsed.cp = scoreValue;
      } else if (scoreType === "mate" && scoreValue !== undefined) {
        parsed.mate = scoreValue;
      }
      index += 2;
      continue;
    }

    if (token === "wdl") {
      const win = readNumber(tokens[index + 1]);
      const draw = readNumber(tokens[index + 2]);
      const loss = readNumber(tokens[index + 3]);
      if (win !== undefined && draw !== undefined && loss !== undefined) {
        const total = Math.max(1, win + draw + loss);
        parsed.wdl = {
          win: win / total,
          draw: draw / total,
          loss: loss / total,
          raw: [win, draw, loss],
        };
      }
      index += 3;
      continue;
    }

    if (token === "nodes") {
      parsed.nodes = readNumber(tokens[index + 1]);
      index += 1;
      continue;
    }

    if (token === "nps") {
      parsed.nps = readNumber(tokens[index + 1]);
      index += 1;
      continue;
    }

    if (token === "time") {
      parsed.millis = readNumber(tokens[index + 1]);
      index += 1;
      continue;
    }

    if (token === "pv") {
      parsed.pv = tokens.slice(index + 1);
      break;
    }
  }

  if (!parsed.depth || (parsed.cp === undefined && parsed.mate === undefined)) {
    return null;
  }

  return parsed;
}

export function scoreFromWhitePerspective(
  pv: Pick<StockfishPv, "cp" | "mate"> | undefined,
  turn: "w" | "b",
) {
  if (!pv) {
    return { cp: 0 as number | undefined, mate: undefined as number | undefined };
  }

  const sign = turn === "w" ? 1 : -1;
  return {
    cp: pv.cp === undefined ? undefined : pv.cp * sign,
    mate: pv.mate === undefined ? undefined : pv.mate * sign,
  };
}

export function formatStockfishScore(
  pv: Pick<StockfishPv, "cp" | "mate"> | undefined,
  turn: "w" | "b",
) {
  const score = scoreFromWhitePerspective(pv, turn);

  if (score.mate !== undefined) {
    return score.mate > 0 ? `#+${score.mate}` : `#${score.mate}`;
  }

  const pawns = (score.cp ?? 0) / 100;
  if (Math.abs(pawns) < 0.05) {
    return "0.0";
  }
  return `${pawns > 0 ? "+" : ""}${pawns.toFixed(1)}`;
}

export function evalShareForWhite(
  pv: Pick<StockfishPv, "cp" | "mate"> | undefined,
  turn: "w" | "b",
) {
  const score = scoreFromWhitePerspective(pv, turn);

  if (score.mate !== undefined) {
    return score.mate > 0 ? 94 : 6;
  }

  const cp = score.cp ?? 0;
  const normalized = 2 / (1 + Math.exp(-0.0036 * cp)) - 1;
  return Math.max(5, Math.min(95, 50 + normalized * 45));
}
